import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Brain, ArrowRight } from 'lucide-react';
import { loadDueReviews } from '@shared/services/review';
import type { ReviewItem } from '@shared/utils/review-schedule';
import { THICK_TILE_STYLE, THICK_TILE_CLASSNAME } from './tileStyles';

const TONE = '#A594F9'; // violet — matches the "Building" readiness band

interface DueForReviewCardProps {
  userId: string;
  onNavigate: (path: string) => void;
}

/**
 * Spaced-review surface (Phase 3). Self-hides when nothing is due. Each row is a
 * quick retrieval rep — spacing + testing are the two best-evidenced ways to
 * make learning stick, so we resurface checkpoints as they come due.
 */
export function DueForReviewCard({ userId, onNavigate }: DueForReviewCardProps) {
  const [items, setItems] = useState<ReviewItem[] | null>(null);

  useEffect(() => {
    let active = true;
    loadDueReviews(userId, 12)
      .then((r) => { if (active) setItems(r); })
      .catch(() => { if (active) setItems([]); });
    return () => { active = false; };
  }, [userId]);

  if (!items || items.length === 0) return null;

  const shown = items.slice(0, 5);
  const extra = items.length - shown.length;

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${THICK_TILE_CLASSNAME} p-5`}
      style={THICK_TILE_STYLE}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{ background: `radial-gradient(ellipse 60% 40% at 80% 50%, ${TONE}22 0%, transparent 70%)` }}
      />
      <div className="relative">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${TONE}22`, color: TONE }}
          >
            <Brain size={22} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-heading text-sm font-bold text-content m-0">Due for review</p>
              <span
                className="px-2 py-0.5 rounded-pill text-[10px] font-body font-bold tracking-wider uppercase"
                style={{ background: `${TONE}22`, color: TONE }}
              >
                {items.length}
              </span>
            </div>
            <p className="text-xs text-content-muted font-body mt-0.5">
              A quick retrieval rep now locks these in — the spacing effect at work.
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {shown.map((it) => (
            <button
              key={`${it.pathId}__${it.nodeId}`}
              type="button"
              onClick={() => onNavigate(`/learn/${it.contentId}`)}
              className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg
                bg-dark-subtle border border-dark-border hover:border-indigo-500/50
                transition-colors cursor-pointer group"
            >
              <span className="flex-1 text-sm text-content-secondary font-body truncate">
                {it.label || 'Review this concept'}
              </span>
              <ArrowRight
                size={15}
                className="text-content-dim group-hover:text-indigo-400 transition-colors shrink-0"
              />
            </button>
          ))}
          {extra > 0 && (
            <p className="text-[11px] text-content-dim font-body pt-1">
              +{extra} more due
            </p>
          )}
        </div>
      </div>
    </motion.section>
  );
}
